import React, { useCallback, FormEvent } from 'react';

import PropTypes from 'prop-types';

import Incrementer from '@adsk/alloy-react-incrementer';
import theme, {
  getInteractiveStyles,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

type StepperProps = Omit<StylableComponent<HTMLInputElement>, 'onChange'> & {
  onChange?: (value?: number) => void;
  value?: number;
  error?: boolean;
  disabled?: boolean;
  max?: number;
  min?: number;
};

const disabledStyle = {
  background: theme.tokens.colors.background.neutral.inactive.value,
  borderColor: theme.tokens.colors.border.inactive.value,
  boxShadow: 'none',
  color: theme.tokens.colors.text.inactive.value,
  cursor: 'not-allowed',
};

const hideSpinButtonStyle = {
  '-webkit-appearance': 'none',
  margin: 0,
};

/**
 * A shorter text input field for numerical values that allows freehand text entries and
 * toggling values up and down. While freehand text editing is supported, values are forced
 * to obey min and max limits (if provided).
 */
const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      className,
      style,
      onChange,
      value,
      error = false,
      disabled = false,
      min,
      max,
      ...props
    },
    ref,
  ) => {
    const formatValue = useCallback(
      (newValue: number) => {
        if (typeof max === 'number' && newValue > max) {
          return max;
        } else if (typeof min === 'number' && newValue < min) {
          return min;
        } else {
          return newValue;
        }
      },
      [min, max],
    );

    const onInputChange = useCallback(
      (e: FormEvent<HTMLInputElement>) => {
        const textValue = e.currentTarget.value;
        const newValue = Number(textValue);

        if (textValue === '') {
          onChange?.(undefined);
        } else if (typeof newValue === 'number') {
          onChange?.(formatValue(newValue));
        }
      },
      [formatValue, onChange],
    );

    const incrementValue = useCallback(
      () => onChange?.(formatValue((value ?? 0) + 1)),
      [onChange, formatValue, value],
    );
    const decrementValue = useCallback(
      () => onChange?.(formatValue((value ?? 0) - 1)),
      [onChange, formatValue, value],
    );

    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            justifyContent: 'space-between',
            width: 84,
            height: 36,
            border: `1px solid ${theme.tokens.colors.border.value}`,
            borderRadius: theme.borderRadius.small,
            paddingRight: theme.spacing.XS,
            paddingLeft: theme.spacing.S,
            ...getInteractiveStyles(disabled),
            ...(disabled && disabledStyle),
            ...(error && {
              borderColor: theme.tokens.colors.border.error.value,
            }),
            '.StepperSelectBox': {
              display: 'none',
            },
          },
          style,
        ]}
      >
        <input
          value={value}
          disabled={disabled}
          onInput={onInputChange}
          role="spinbutton"
          aria-label={props['aria-labelledby'] ? undefined : 'stepper'} // If aria-labelledby is provided, aria-label is not needed
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-invalid={error}
          type="number"
          step="1"
          min={min}
          max={max}
          css={[
            {
              border: 'none',
              outline: 'none',
              color: theme.tokens.colors.text.primary.value,
              width: 36,
              ...(disabled && disabledStyle),
              // hide default input arrows
              '&::-webkit-outer-spin-button': hideSpinButtonStyle,
              '&::-webkit-inner-spin-button': hideSpinButtonStyle,
              '-moz-appearance': 'textfield',
            },
            theme.typography.labelMedium,
          ]}
          {...props}
        />
        <Incrementer
          disabledUp={
            (typeof max === 'number' && (value ?? 0) >= max) || disabled
          }
          disabledDown={
            (typeof min === 'number' && (value ?? 0) <= min) || disabled
          }
          onClickUp={incrementValue}
          onClickDown={decrementValue}
        />
      </div>
    );
  },
);
Stepper.displayName = 'Stepper';

Stepper.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The value */
  value: PropTypes.number,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** maximum allowed stepper value */
  max: PropTypes.number,
  /** minimum allowed stepper value */
  min: PropTypes.number,
  /** callback when selected value changed */
  onChange: PropTypes.func,
};

export default Stepper;
